import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowsLeftRight, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { byProductName } from "../lib/utils";

// v412 God-mode transfer — owner/admin moves stock STRAIGHT from one
// distributor to another (e.g. Robert shipped BPC to Haven directly),
// no round-trip through the master account. If the source's in-system
// count is short, the move still goes through (force) and their stock
// shows negative until it's topped up.
export default function GodModeTransferDialog({ onClose, onDone }) {
  const { t } = useTranslation();
  const [distributors, setDistributors] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [inventory, setInventory] = useState(null); // null = loading
  const [productId, setProductId] = useState("");
  const [destId, setDestId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [markup, setMarkup] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/distributors")
      .then(({ data }) => setDistributors(data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!sourceId) { setInventory(null); return; }
    setInventory(null);
    setProductId("");
    api.get(`/distributors/${sourceId}/inventory`)
      .then(({ data }) => setInventory([...(data || [])].sort(byProductName)))
      .catch((e) => { toast.error(formatApiError(e)); setInventory([]); });
  }, [sourceId]);

  // Never let source == destination.
  useEffect(() => {
    if (destId && destId === sourceId) setDestId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  // Prefill markup with the recipient's default margin (same as Give dialog).
  useEffect(() => {
    if (!destId) return;
    const rec = distributors.find((d) => d.id === destId);
    // Percent margins depend on each product's base price — no flat prefill.
    const flat = rec && (rec.margin_type || "flat") === "flat";
    setMarkup(flat && Number(rec.margin_surcharge) > 0 ? String(rec.margin_surcharge) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId]);

  const sourceOptions = useMemo(
    () => distributors.filter((d) => !d.is_personal),
    [distributors]
  );
  // Personal entries are only valid recipients of their OWN parent's stock.
  const destOptions = useMemo(
    () => distributors.filter(
      (d) => d.id !== sourceId && (!d.is_personal || d.owner_id === sourceId)
    ),
    [distributors, sourceId]
  );

  const optionLabel = (d) => {
    const bits = [d.name];
    if (d.distributor_code) bits.push(d.distributor_code);
    if ((d.depth ?? 1) > 1 && d.parent_name) bits.push(t("assign.under", { name: d.parent_name }));
    else if (d.role && d.role !== "distributor") bits.push(t("distributors.partner_badge"));
    return bits.join(" · ");
  };

  const row = (inventory || []).find((p) => p.id === productId);
  const onHand = row?.current_stock ?? 0;
  const qty = Math.max(1, Number(quantity) || 0);
  const shortStock = !!row && qty > onHand;
  const fromName = distributors.find((d) => d.id === sourceId)?.name || "";
  const toName = distributors.find((d) => d.id === destId)?.name || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!sourceId || !destId || !row) return;
    setBusy(true);
    try {
      await api.post("/products/assign", {
        distributor_id: destId,
        product_id: row.id,
        quantity: qty,
        transfer_type: "consignment",
        holder_user_id: sourceId,
        markup_per_unit: Number(markup) > 0 ? Number(markup) : undefined,
        note: note.trim().slice(0, 500) || null,
        force: shortStock || undefined,
      });
      toast.success(t("godtransfer.success", { n: qty, name: row.name, from: fromName, to: toName }));
      onDone?.();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="god-transfer-dialog"
      >
        <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <ArrowsLeftRight size={22} weight="bold" className="text-[#8a6a1c]" />
          {t("godtransfer.title")}
        </h2>
        <p className="text-sm text-zinc-600 mb-4">{t("godtransfer.subtitle")}</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("godtransfer.from_label")}</label>
            <select
              className="input-brutal"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              data-testid="god-transfer-source-select"
            >
              <option value="">— {t("godtransfer.pick_source")} —</option>
              {sourceOptions.map((d) => (
                <option key={d.id} value={d.id}>{optionLabel(d)}</option>
              ))}
            </select>
          </div>

          {sourceId && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("godtransfer.product_label")}</label>
              {inventory === null ? (
                <p className="text-sm text-zinc-500 py-1">{t("common.loading")}</p>
              ) : inventory.length === 0 ? (
                <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid="god-transfer-no-inventory">
                  {t("godtransfer.no_inventory")}
                </div>
              ) : (
                <select
                  className="input-brutal"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  data-testid="god-transfer-product-select"
                >
                  <option value="">— {t("godtransfer.pick_product")} —</option>
                  {inventory.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {t("godtransfer.on_hand", { n: p.current_stock ?? 0 })}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {productId && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("godtransfer.to_label")}</label>
              <select
                className="input-brutal"
                value={destId}
                onChange={(e) => setDestId(e.target.value)}
                data-testid="god-transfer-dest-select"
              >
                <option value="">— {t("godtransfer.pick_dest")} —</option>
                {destOptions.map((d) => (
                  <option key={d.id} value={d.id}>{optionLabel(d)}</option>
                ))}
              </select>
            </div>
          )}

          {productId && destId && (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1">{t("godtransfer.quantity")}</label>
                <BrutalInput
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  data-testid="god-transfer-qty-input"
                />
              </div>

              {shortStock && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3"
                  data-testid="god-transfer-override-warning"
                >
                  <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs font-medium text-amber-900">
                    {t("godtransfer.override_warning", { n: onHand })}
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                <label className="block text-xs font-semibold mb-1 text-emerald-900">{t("godtransfer.markup_label")}</label>
                <BrutalInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={markup}
                  onChange={(e) => setMarkup(e.target.value)}
                  placeholder="0.00"
                  data-testid="god-transfer-markup-input"
                />
                <p className="text-[11px] text-emerald-800 mt-1">{t("godtransfer.markup_hint")}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">{t("godtransfer.note_label")}</label>
                <BrutalInput
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("godtransfer.note_placeholder")}
                  maxLength={500}
                  data-testid="god-transfer-note-input"
                />
              </div>

              <p className="text-sm font-semibold text-[#8a6a1c]" data-testid="god-transfer-route-preview">
                {t("godtransfer.route", { from: fromName, to: toName })}
              </p>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={busy || !sourceId || !destId || !row}
              data-testid="god-transfer-submit"
            >
              {busy ? t("godtransfer.moving") : t("godtransfer.submit")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}
